import type { BuyerAssignmentQueueParams, BuyerLeadSummaryDto } from "@/api/buyers";
import type { ListFilterDef } from "@/hooks/useListFilters";
import { ASSIGNMENT_QUEUE_FILTERS, type ListViewPreset } from "./buyerFilters";

// The assignment queue's saved views (D5) and the descriptor the page hands to
// `useListFilters`. Beside `buyerFilters.ts` rather than inside it: that file
// holds the vocabularies the four lists SHARE, and a view is one page's own.

/**
 * The saved views.
 *
 * <p>`waiting` is the server's own stale flag. 🔴 `duplicates` sends NOTHING:
 * `BuyerAssignmentQueueQuery` has no duplicate predicate, so the page narrows
 * the rows it holds to those the funnel's dedupe probe flagged
 * (`suspectedDuplicateOfBuyerLeadId`) and says so beside the table. A queue is
 * small by construction — every row on it is unowned work — so a page-local
 * narrowing is honest there in a way it would not be on the buyers list.</p>
 */
export const QUEUE_VIEWS: ListViewPreset<BuyerAssignmentQueueParams>[] = [
  { key: "whole-queue", label: "Whole queue", params: {} },
  { key: "waiting", label: "Waiting too long", params: { staleOnly: true } },
  {
    key: "duplicates",
    label: "Possible duplicates",
    params: {},
    description: "Rows the funnel flagged as a likely repeat of an existing buyer.",
  },
];

export const DEFAULT_QUEUE_VIEW = "whole-queue";

const QUEUE_VIEW_TOKENS = new Set(QUEUE_VIEWS.map((v) => v.key));

/** Rows the funnel flagged as a likely repeat. The page-local half of the
 *  `duplicates` view, and the only half there is. */
export function isSuspectedDuplicate(b: BuyerLeadSummaryDto): boolean {
  return Boolean(b.suspectedDuplicateOfBuyerLeadId);
}

const viewFilter: ListFilterDef<string> = {
  control: "hidden",
  defaultValue: "",
  parse: (raw) => (QUEUE_VIEW_TOKENS.has(raw) ? raw : undefined),
};

/**
 * The advertisement a lead is attributed to, by NAME.
 *
 * <p>🔴 Page-local, like the duplicates view: the queue endpoint accepts no
 * advertisement param, and the row carries the name rather than an id the
 * server would key on. The options are the distinct names on the rows in hand,
 * so a name that matches nothing on this page narrows it to nothing — which the
 * page says rather than hides.</p>
 */
const advertisementFilter: ListFilterDef<string> = {
  control: "hidden",
  defaultValue: "",
  label: "Advertisement filter",
  parse: (raw) => raw || undefined,
};

/** The shared descriptor plus this page's view and advertisement params. Every
 *  URL the shared descriptor accepted is still accepted, byte for byte. */
export const QUEUE_LIST_FILTERS = {
  ...ASSIGNMENT_QUEUE_FILTERS,
  view: viewFilter,
  advertisement: advertisementFilter,
};
